import { useEffect, useRef, useState } from 'react';
import { trpc } from '@/data-loaders';
import { VisuallyHidden } from '@radix-ui/react-visually-hidden';
import { SheetContent, SheetDescription, SheetTitle } from '@/components/ui/sheet';
import { useHandleError, useAcademicWorkloadAllocations } from '@/hooks';
import { useAwmsStore } from '@/store';
import { FILTERS } from '@/constants';
import { AcademicWorkloadAllocationsWithCalculations } from '#server/models';
import { AcademicWorkloadPanel } from './AcademicWorkloadPanel';
import { AcademicWorkloadAllocations } from './AcademicWorkloadAllocations';
import { Loader } from '@/components/Common';

export const AcademicWorkload = ({
  setHasSavedAcademicWorkload,
}: {
  setHasSavedAcademicWorkload: React.Dispatch<React.SetStateAction<boolean>>;
}) => {
  const sheetRef = useRef<HTMLDivElement | null>(null);
  const selectedStaff = useAwmsStore((state) => state.selectedStaff);
  const selectedStaffId = selectedStaff?.id ?? 0;
  const searchFilters = useAwmsStore((state) => state.searchFilters);
  const selectedSchool = searchFilters[FILTERS.SCHOOL];
  const selectedSchoolId = selectedSchool?.value ?? 0;
  const [backendVersion, setBackendVersion] = useState(0);
  const trpcUtils = trpc.useUtils();
  const handleError = useHandleError();
  const [isPageLoading, setIsPageLoading] = useState(true);
  const [allocationsWithCalculatedHours, setAllocationsWithCalculatedHours] =
    useState<AcademicWorkloadAllocationsWithCalculations>({} as AcademicWorkloadAllocationsWithCalculations);

  const handleInvalidateAndRefetch = async (schoolId: number, academicId: number) => {
    await trpcUtils.academic.getAcademicWorkloadAllocations.invalidate({
      schoolId: schoolId,
      academicId: academicId,
    });
    await trpcUtils.academic.getAcademicWorkloadAllocations.refetch({
      schoolId: schoolId,
      academicId: academicId,
    });
  };

  const {
    data: allocations,
    isLoading: isAllocationsLoading,
    error: allocationsError,
  } = useAcademicWorkloadAllocations(selectedSchoolId, selectedStaffId);

  handleError([allocationsError]);

  useEffect(() => {
    if (allocations) {
      setAllocationsWithCalculatedHours(allocations);
      // keep backend fetching data version
      setBackendVersion((v: number) => v + 1);
    }
  }, [allocations]);

  useEffect(() => {
    setIsPageLoading(isAllocationsLoading);
  }, [isAllocationsLoading]);

  if (isPageLoading) {
    return <Loader />;
  }

  if (!selectedSchool || !allocations) {
    throw new Error('No school or allocation data found');
  }

  return (
    <SheetContent
      onInteractOutside={(event) => event.preventDefault()}
      ref={sheetRef}
      className="mt-[60px] flex max-h-[calc(100vh-60px)] !w-screen !max-w-none flex-col items-start justify-start overflow-y-auto
        bg-secondary-grey-04 p-0 focus-visible:outline-none"
    >
      <VisuallyHidden>
        <SheetTitle>This is hidden for ensuring accessibility compliance</SheetTitle>
        <SheetDescription>This is hidden for ensuring accessibility compliance</SheetDescription>
      </VisuallyHidden>

      <div className="flex h-full w-full flex-row items-stretch overflow-auto bg-gray-100">
        <div className="w-[240px] bg-gray-100">
          <AcademicWorkloadPanel
            selectedSchool={selectedSchool}
            allocationsWithCalculatedHours={allocationsWithCalculatedHours}
          />
        </div>
        <div className="flex-1">
          <AcademicWorkloadAllocations
            schoolId={selectedSchoolId}
            allocationsWithCalculatedHours={allocationsWithCalculatedHours}
            setAllocationsWithCalculatedHours={setAllocationsWithCalculatedHours}
            backendVersion={backendVersion}
            handleInvalidateAndRefetch={handleInvalidateAndRefetch}
            setHasSavedAcademicWorkload={setHasSavedAcademicWorkload}
          />
        </div>
      </div>
    </SheetContent>
  );
};
